import { Optional, SkipSelf } from '@angular/core';

import { Cookie } from './cookie';

// TODO scope by admin. we don't have access to admin id at the moment from
// elements, but, eventually, it'll be good to change it.

export interface LocalStorageAccessor {
  get: (key: string, defaultValue?: any) => any;
  set: (key: string, value: any) => Promise<boolean>;
  has: (key: string) => any;
  remove: (key: string) => void;
}

export function createScopedLocalStorageAccessor(companyId: string): LocalStorageAccessor {
  const getKey = (key: string) => `${companyId}#${key}`;
  const storage = localStorage;

  // As we don't want to block UI when writing to localStorage, so here is small workaround.
  // in Chrome there might be slightly longer delay than in other browsers because of:
  // https://bugs.chromium.org/p/chromium/issues/detail?id=811451
  // but it shouldn't be big problem because we don't do something like read -> write -> read (and we shouldn't anyway :D)
  const scheduler =
    typeof requestIdleCallback !== 'undefined' ? requestIdleCallback : requestAnimationFrame;

  return {
    get: (key: string) => {
      const raw = storage.getItem(getKey(key));
      if (raw === null) return null;

      try {
        return JSON.parse(raw);
      } catch (err) {
        return raw;
      }
    },
    set: (key: string, value: any) =>
      new Promise((res, rej) => {
        if (value !== undefined) {
          scheduler(() => {
            storage.setItem(getKey(key), JSON.stringify(value));
            res(true);
          });
        } else {
          rej();
        }
      }),
    has: (key: string) => storage.hasOwnProperty(getKey(key)),
    remove: (key: string) => storage.removeItem(getKey(key)),
  };
}

export class LocalStorageService implements LocalStorageAccessor {
  constructor(private accessor: LocalStorageAccessor) {}
  public get(key: string, defaultValue: any = null): any {
    return this.accessor.get(key) || defaultValue;
  }
  public has(key: string): boolean {
    return this.accessor.has(key);
  }
  public set(key: string, value: any): Promise<boolean> {
    return this.accessor.set(key, value);
  }
  public remove(key: string): any {
    return this.accessor.remove(key);
  }
}

export function provideScopedLocalStorage(
  existingService?: LocalStorageService,
): LocalStorageService {
  if (existingService) {
    return existingService;
  }
  const companyId = Cookie.get('company');

  return new LocalStorageService(createScopedLocalStorageAccessor(companyId));
}

export const SCOPED_LOCALSTORAGE_PROVIDER = {
  provide: LocalStorageService,
  useFactory: provideScopedLocalStorage,
  deps: [[new Optional(), new SkipSelf(), LocalStorageService]],
};
